import type { TourProps } from "src/assets/imports/antdComponents";
import type { TFunction } from "i18next";

export const PERSONA_TUTORIAL_VERSION = "v5";
export const PERSONA_TUTORIAL_STORAGE_PREFIX = "kural.personas.tour";

export type PersonaTutorialPage = "home" | "setup" | "chat" | "chatCompare" | "chatFocusGroup";

export type PersonaTutorialScope = {
  profileId?: string;
  userId?: string;
};

export type PersonaTutorialState = {
  seenPages?: Partial<Record<PersonaTutorialPage, true>>;
  version?: string;
};

export type PersonaTutorialStepDefinition = {
  anchorId: string;
  description: string;
  placement?: TourProps["placement"];
  title: string;
};

export const PERSONA_TOUR_ANCHORS = {
  homeAddProject: "persona-tour-home-add-project",
  homeProjectList: "persona-tour-home-project-list",
  homeSearch: "persona-tour-home-search",
  help: "persona-tour-help",
  setupAddPersona: "persona-tour-setup-add-persona",
  setupAttachStudies: "persona-tour-setup-attach-studies",
  setupChat: "persona-tour-setup-chat",
  setupPersonaList: "persona-tour-setup-persona-list",
  setupSegmentationUpload: "persona-tour-setup-segmentation-upload",
  chatComposer: "persona-tour-chat-composer",
  chatExportTranscript: "persona-tour-chat-export-transcript",
  chatCompareCreateRun: "persona-tour-chat-compare-create-run",
  chatCompareComposer: "persona-tour-chat-compare-composer",
  chatCompareComposerConcept: "persona-tour-chat-compare-composer-concept",
  chatCompareConceptPreview: "persona-tour-chat-compare-concept-preview",
  chatCompareFocusGroup: "persona-tour-chat-compare-focus-group",
  chatCompareIndependentResponses: "persona-tour-chat-compare-independent-responses",
  chatCompareRunDetail: "persona-tour-chat-compare-run-detail",
  chatCompareRunList: "persona-tour-chat-compare-run-list",
  chatCompareSessionActions: "persona-tour-chat-compare-session-actions",
  chatCompareStartFocusGroup: "persona-tour-chat-compare-start-focus-group",
  chatCompareSynthesis: "persona-tour-chat-compare-synthesis",
  chatGroundingStatus: "persona-tour-chat-grounding-status",
  chatModeSwitch: "persona-tour-chat-mode-switch",
  chatNewChat: "persona-tour-chat-new-chat",
  chatPersonaSelector: "persona-tour-chat-persona-selector",
  chatReportContext: "persona-tour-chat-report-context",
  chatSaveDiscourse: "persona-tour-chat-save-discourse",
  chatUploadFile: "persona-tour-chat-upload-file",
  chatVoiceMode: "persona-tour-chat-voice-mode",
} as const;

const DEFAULT_SCOPE = "default";

const getDefaultStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

export const getPersonaTutorialStorageKey = (scope: PersonaTutorialScope): string => {
  const profilePart = scope.profileId?.trim() || DEFAULT_SCOPE;
  const userPart = scope.userId?.trim() || DEFAULT_SCOPE;
  return `${PERSONA_TUTORIAL_STORAGE_PREFIX}.${PERSONA_TUTORIAL_VERSION}.${profilePart}.${userPart}`;
};

export const isPersonaTutorialScopeReady = (scope: PersonaTutorialScope): boolean =>
  Boolean(scope.profileId?.trim() || scope.userId?.trim());

const getStoredPersonaTutorialState = (
  scope: PersonaTutorialScope,
  storage?: Storage,
): PersonaTutorialState => {
  const targetStorage = storage ?? getDefaultStorage();
  if (!targetStorage) return { seenPages: {}, version: PERSONA_TUTORIAL_VERSION };

  try {
    const raw = targetStorage.getItem(getPersonaTutorialStorageKey(scope));
    if (!raw) return { seenPages: {}, version: PERSONA_TUTORIAL_VERSION };
    const parsed = JSON.parse(raw) as PersonaTutorialState;
    return {
      seenPages: parsed.seenPages ?? {},
      version: parsed.version || PERSONA_TUTORIAL_VERSION,
    };
  } catch {
    return { seenPages: {}, version: PERSONA_TUTORIAL_VERSION };
  }
};

export const hasSeenPersonaTutorialPage = (
  page: PersonaTutorialPage,
  scope: PersonaTutorialScope,
  storage?: Storage,
): boolean => Boolean(getStoredPersonaTutorialState(scope, storage).seenPages?.[page]);

export const markPersonaTutorialPageSeen = (
  page: PersonaTutorialPage,
  scope: PersonaTutorialScope,
  storage?: Storage,
): void => {
  const targetStorage = storage ?? getDefaultStorage();
  if (!targetStorage) return;

  try {
    const state = getStoredPersonaTutorialState(scope, targetStorage);
    targetStorage.setItem(
      getPersonaTutorialStorageKey(scope),
      JSON.stringify({
        ...state,
        seenPages: {
          ...(state.seenPages ?? {}),
          [page]: true,
        },
        version: PERSONA_TUTORIAL_VERSION,
      }),
    );
  } catch {
    // localStorage can be unavailable in locked-down browser contexts.
  }
};

export const getPersonaTutorialStepDefinitions = (
  page: PersonaTutorialPage,
  t: TFunction,
): PersonaTutorialStepDefinition[] => {
  if (page === "home") {
    return [
      {
        anchorId: PERSONA_TOUR_ANCHORS.homeAddProject,
        placement: "bottomRight",
        title: t("Create a persona project"),
        description: t("Start here when you want to define a new set of personas for a study or business question."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.homeSearch,
        placement: "bottom",
        title: t("Find existing projects"),
        description: t("Use search to quickly return to a persona project by name or tag."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.homeProjectList,
        placement: "top",
        title: t("Open or manage a project"),
        description: t("Project cards show persona and study counts. Owners can also manage collaborators from the card."),
      },
    ];
  }

  if (page === "setup") {
    return [
      {
        anchorId: PERSONA_TOUR_ANCHORS.setupAddPersona,
        placement: "bottomRight",
        title: t("Add personas manually"),
        description: t("Create persona cards one at a time, then edit the name, definition, and professional avatar for each archetype."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.setupSegmentationUpload,
        placement: "bottomRight",
        title: t("Import a segmentation report"),
        description: t("Upload a PPTX segmentation deck, review detected segments, and import only the personas you want. Existing personas are retained."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.setupPersonaList,
        placement: "top",
        title: t("Review persona cards"),
        description: t("Each card contains the persona definition and the studies or transcripts attached as inspiration."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.setupAttachStudies,
        placement: "left",
        title: t("Attach study evidence"),
        description: t("Use Attach Studies to choose transcripts, review metadata, and apply shared transcript metadata when needed before building persona memory."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.setupChat,
        placement: "bottomRight",
        title: t("Move to Symposium"),
        description: t("Once personas and evidence are ready, open Symposium to use Single Persona Discourse, Multi-Persona Discourse, or Focus Group."),
      },
    ];
  }

  if (page === "chatCompare" || page === "chatFocusGroup") {
    const isFocusGroupTour = page === "chatFocusGroup";

    return [
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatModeSwitch,
        placement: "bottomRight",
        title: t("Switch Symposium modes"),
        description: isFocusGroupTour
          ? t("Use this control to move between Focus Group and the other Symposium modes.")
          : t("Use this control to move between Single Persona Discourse, Multi-Persona Discourse, and Focus Group."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareCreateRun,
        placement: "bottomRight",
        title: isFocusGroupTour
          ? t("Create a Focus Group")
          : t("Create a Multi-Persona Discourse"),
        description: isFocusGroupTour
          ? t("Start a saved Focus Group by selecting multiple personas, choosing a first speaker, and adding the opening discussion prompt.")
          : t("Start a saved Multi-Persona Discourse by selecting multiple personas and adding the same question or reviewed message concept for each one."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareRunList,
        placement: "right",
        title: t("Review saved sessions"),
        description: isFocusGroupTour
          ? t("This rail lists Focus Groups separately from Single Persona Discourses. Editors can also delete sessions that are no longer needed.")
          : t("This rail lists Multi-Persona Discourses and Focus Groups separately from Single Persona Discourses. Editors can also delete sessions that are no longer needed."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareRunDetail,
        placement: "top",
        title: t("Inspect the active round"),
        description: isFocusGroupTour
          ? t("The detail area shows the current turn, selected personas, progress, moderator guidance, persona responses, and the analysis summary.")
          : t("The detail area shows the current round, selected personas, progress, reviewed concepts, individual responses, and the analysis summary."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareSessionActions,
        placement: "bottomRight",
        title: t("Manage the session"),
        description: isFocusGroupTour
          ? t("Rename the Focus Group, export the session as a Word document, retry failed runs, or continue the discussion when the current turn is complete.")
          : t("Rename the session, export the full Multi-Persona Discourse as a Word document, retry failed runs, or continue in Focus Group when the independent round is complete."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareConceptPreview,
        placement: "top",
        title: t("Review message concepts"),
        description: t("Uploaded XLSX, CSV, PPTX, or image concepts appear here with a compact preview so business users can confirm what was tested."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareIndependentResponses,
        placement: "top",
        title: isFocusGroupTour
          ? t("Read Focus Group responses")
          : t("Read individual responses"),
        description: isFocusGroupTour
          ? t("Personas participate in a moderated sequence. Review each turn to see the current speaker, reactions, and grounded rationale.")
          : t("Each persona answers independently. In later rounds, each persona sees only its own prior Multi-Persona Discourse responses unless you start a Focus Group."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareSynthesis,
        placement: "top",
        title: t("Read the analysis summary"),
        description: isFocusGroupTour
          ? t("This user-facing summary captures the discussion themes, tensions, moderator signals, and next-step implications.")
          : t("This user-facing summary separates persona-by-persona readouts from similarities, differences, tensions, and implications."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareStartFocusGroup,
        placement: "bottomRight",
        title: t("Start a Focus Group"),
        description: t("When the independent round is complete, start a Focus Group. Personas react to a neutral Facilitator Brief, and you can add an optional focus instruction."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareFocusGroup,
        placement: "top",
        title: t("Review Focus Group responses"),
        description: t("The Focus Group section shows the Facilitator Brief, optional focus instruction, and each persona's follow-up reaction."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareComposer,
        placement: "top",
        title: isFocusGroupTour
          ? t("Continue the Focus Group")
          : t("Ask the next question"),
        description: isFocusGroupTour
          ? t("Use the composer to continue the same Focus Group with the next topic or moderator-guided follow-up.")
          : t("Continue the same session from here. The next question creates another round with the same personas and persona-private continuity."),
      },
      {
        anchorId: PERSONA_TOUR_ANCHORS.chatCompareComposerConcept,
        placement: "top",
        title: t("Attach a message concept"),
        description: t("For follow-up rounds, paste or upload message concepts using the same file guardrails as Discourse attachments."),
      },
    ];
  }

  return [
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatNewChat,
      placement: "right",
      title: t("Start or select Discourses"),
      description: t("Create a new one-on-one Discourse or return to saved persona Discourses from this sidebar."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatPersonaSelector,
      placement: "bottomRight",
      title: t("Choose the persona"),
      description: t("Pick which persona should answer. Saved Discourses keep the original persona locked for consistency."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatModeSwitch,
      placement: "bottomRight",
      title: t("Switch Symposium modes"),
      description: t("Use Multi-Persona Discourse when multiple personas should answer the same prompt, or Focus Group when you want moderated group discussion."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatGroundingStatus,
      placement: "bottom",
      title: t("Check memory status"),
      description: t("These banners show whether transcript-based persona memory is ready, building, or needs attention."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatReportContext,
      placement: "bottom",
      title: t("Refresh report context"),
      description: t("Editors can refresh report-derived persona context when new report evidence should augment the persona."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatUploadFile,
      placement: "topRight",
      title: t("Attach Discourse context"),
      description: t("Attach one supported file to a Discourse when you want the persona to consider extra context for that conversation."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatVoiceMode,
      placement: "topRight",
      title: t("Use voice mode"),
      description: t("Start a voice session when you want to speak with the selected persona. Stop the session before switching personas or modes."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatSaveDiscourse,
      placement: "bottomRight",
      title: t("Save the Discourse"),
      description: t("Save a useful conversation so you can rename, revisit, or continue it later with the same persona."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatExportTranscript,
      placement: "bottomRight",
      title: t("Export the transcript"),
      description: t("Export saved conversation turns and grounding evidence as a Word document for business review."),
    },
    {
      anchorId: PERSONA_TOUR_ANCHORS.chatComposer,
      placement: "top",
      title: t("Ask in Discourse"),
      description: t("Type your question here. Grounded text in responses is highlighted when it is supported by persona evidence."),
    },
  ];
};
